import { API_BASE_URL, API_KEY_STORAGE, TORN_API_URL } from "./constants";

export type ApiError = { status: number; detail: string };

function getStoredKey(): string | null {
  return localStorage.getItem(API_KEY_STORAGE);
}

export interface FetchOptions extends Omit<RequestInit, "body"> {
  /** When set, will be JSON-serialized and the Content-Type header added. */
  body?: unknown;
}

/**
 * Fetch wrapper for the bot API. Injects X-API-Key automatically.
 * Throws an ApiError ({status, detail}) on non-2xx responses.
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: FetchOptions = {},
): Promise<T> {
  const apiKey = getStoredKey();
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) ?? {}),
  };

  if (apiKey) {
    headers["X-API-Key"] = apiKey;
  }

  let body: BodyInit | undefined;
  if (options.body !== undefined && options.body !== null) {
    if (
      typeof options.body === "object" &&
      !(options.body instanceof FormData) &&
      !(options.body instanceof Blob)
    ) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(options.body);
    } else {
      body = options.body as BodyInit;
    }
  }

  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    body,
  });

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const data = await res.json();
      detail = data.detail || res.statusText;
    } catch {
      // body wasn't JSON — keep statusText
    }
    const err: ApiError = { status: res.status, detail };
    throw err;
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Direct Torn v2 API fetch using the user's stored key. Used for routes
 * that need data the bot doesn't proxy (e.g. faction roster). Throws
 * `ApiError` on non-2xx or Torn-side error envelopes.
 */
export async function tornFetch<T = unknown>(
  selections: string,
  endpoint = "faction",
): Promise<T> {
  const apiKey = localStorage.getItem(API_KEY_STORAGE);
  if (!apiKey) throw { status: 401, detail: "No API key" } satisfies ApiError;

  const url = `${TORN_API_URL}/${endpoint}?selections=${encodeURIComponent(selections)}&key=${apiKey}`;
  let res: Response;
  try {
    res = await fetch(url);
  } catch {
    throw { status: 0, detail: "Network error contacting Torn API" } satisfies ApiError;
  }

  const contentType = res.headers.get("Content-Type") ?? "";
  if (!contentType.includes("application/json")) {
    throw {
      status: res.status,
      detail: "Torn API returned an unexpected response.",
    } satisfies ApiError;
  }

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    throw {
      status: res.status,
      detail: "Torn API returned invalid JSON.",
    } satisfies ApiError;
  }

  if (!res.ok) {
    const detail =
      (data as { error?: { error?: string } }).error?.error ?? res.statusText;
    throw { status: res.status, detail } satisfies ApiError;
  }

  // Torn returns 200 with `error: { code, error }` for app-level failures.
  const apiError = (data as { error?: { code?: number; error?: string } })
    .error;
  if (apiError) {
    throw {
      status: 400,
      detail: apiError.error ?? "Torn API error",
    } satisfies ApiError;
  }
  return data as T;
}
